import {
  useState,
  useEffect
} from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  createSchool
} from "../api/schoolApi";
import {
  useAuth
} from "../context/AuthContext";
import {
  GraduationCap,
  School,
  Phone,
  MapPin,
  IdCard,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Info,
  ShieldCheck
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Textarea,
  Modal
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const SchoolSetup = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [formData, setFormData] = useState({
    schoolName: "",
    mobile: "",
    address: "",
    regId: "" 
  });

  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [progressWidth, setProgressWidth] = useState("w-0");

  const toast = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      await createSchool({
        schoolName: formData.schoolName,
        mobile: formData.mobile,
        address: formData.address,
        regId: formData.regId
      });

      await refreshUser();
      setShowSuccessModal(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to create school profile. Please verify your inputs."
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showSuccessModal) {
      const progressTimer = setTimeout(() => setProgressWidth("w-full"), 100);
      const redirectTimer = setTimeout(() => {
        navigate("/school-home");
      }, 3100);

      return () => {
        clearTimeout(progressTimer);
        clearTimeout(redirectTimer);
      };
    }
  }, [showSuccessModal, navigate]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col justify-between antialiased selection:bg-primary-100 selection:text-primary-900">
      
      {/* Top Navigation / Header */}
      <header className="sticky top-0 z-40 w-full h-16 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800 px-4 sm:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="text-base sm:text-lg font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            EduFlow
          </span>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Step 3 of 3
          </span>
          <div className="flex items-center gap-1">
            <div className="h-1.5 w-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-1.5 w-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-1.5 w-10 rounded-full bg-neutral-900 transition-all duration-300" />
          </div>
        </div>
      </header>

      {/* Main Content Layout */}
      <main className="w-full max-w-6xl mx-auto my-auto p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          
          {/* Left Hero / Context Column */}
          <div className="lg:col-span-5 space-y-4">
            <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-neutral-100/60 dark:bg-neutral-900/60">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-100 shadow-xs">
                    <School className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                      Enterprise Infrastructure Setup
                    </h2>
                    <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-1.5 leading-relaxed">
                      Establish your definitive campus profile. This configuration forms the root identity layer across academic schedules, payroll systems, and digital grading books.
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-neutral-200/80 flex items-center justify-between text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-neutral-700 dark:text-neutral-200" />
                    System Encryption: Active
                  </span>
                  <span className="font-mono text-neutral-900 dark:text-neutral-100">TLS 1.3 / AES-256</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Form Column */}
          <div className="lg:col-span-7">
            <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
              <CardHeader className="pt-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
                <CardTitle className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                  School Setup
                </CardTitle>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                  Complete your administrative operational profile
                </p>
              </CardHeader>

              <CardContent className="p-6 space-y-4">
                

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="School Name *"
                    id="schoolName"
                    name="schoolName"
                    type="text"
                    required
                    value={formData.schoolName}
                    onChange={handleChange}
                    placeholder="e.g. St. Andrews International School"
                    leftIcon={<School className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Mobile Number *"
                      id="mobile"
                      name="mobile"
                      type="tel"
                      required
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      leftIcon={<Phone className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                    />

                    <Input
                      label="Registration ID (optional)"
                      id="regId"
                      name="regId"
                      type="text"
                      value={formData.regId}
                      onChange={handleChange}
                      placeholder="REG-2026-XXXX"
                      leftIcon={<IdCard className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                    />
                  </div>

                  <Textarea
                    label="School Address *"
                    id="address"
                    name="address"
                    required
                    rows={3}
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter the full campus physical address..."
                    leftIcon={<MapPin className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                  />

                  <div className="pt-2 space-y-3">
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={loading}
                      rightIcon={!loading && <ArrowRight className="w-3.5 h-3.5" />}
                      className="w-full text-xs font-semibold py-2.5"
                    >
                      {loading ? "Generating Workspace..." : "Create School Profile"}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => navigate(-1)}
                      leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}
                      className="w-full text-xs border border-neutral-200 dark:border-neutral-800 py-2.5"
                    >
                      Previous Step
                    </Button>
                  </div>
                </form>

                <div className="flex items-start gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400">
                  <Info className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-normal">
                    By finalizing this setup profile, you agree to EduFlow's{" "}
                    <span className="text-neutral-900 dark:text-neutral-100 font-semibold hover:underline cursor-pointer">
                      Terms of Service
                    </span>{" "}
                    and{" "}
                    <span className="text-neutral-900 dark:text-neutral-100 font-semibold hover:underline cursor-pointer">
                      Privacy Policy
                    </span>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
          © {new Date().getFullYear()} EduFlow ERP System. All rights reserved.
        </p>
      </footer>

      {/* Success Modal */}
      <Modal 
        isOpen={showSuccessModal} 
        onClose={() => {}} 
        title=""
        size="sm"
      >
        <div className="text-center space-y-4 py-2">
          <div className="w-16 h-16 bg-success-50 text-success-700 rounded-full flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 className="w-8 h-8" />
          </div>

          <div className="space-y-1">
            <h2 className="text-base font-bold text-neutral-900 dark:text-neutral-100">Setup Complete!</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-xs mx-auto">
              Welcome to the EduFlow family. Your school dashboard is ready. Redirecting you to your new workspace.
            </p>
          </div>

          <div className="w-full h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden mt-2">
            <div className={`h-full bg-neutral-900 transition-all duration-[3000ms] ease-out ${progressWidth}`} />
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default SchoolSetup;